<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h4 class="m-0">Static pages</h4>
      <el-button type="primary" size="small" @click="createPage">Create</el-button>
    </div>

    <card>
      <div class="d-flex align-items-center mb-3" style="gap: 16px; flex-wrap: wrap;">
        <el-input v-model="search"
                  placeholder="Search by title, slug or id..."
                  clearable
                  style="max-width: 360px" />
        <el-select v-model="visibility" style="width: 180px">
          <el-option label="All pages" value="all" />
          <el-option label="Visible only" value="visible" />
          <el-option label="Hidden only" value="hidden" />
        </el-select>
        <span class="text-muted" style="font-size: 12px;">
          {{ filteredDocs.length }} of {{ docs.length }}
        </span>
      </div>

      <el-table :data="filteredDocs" v-loading="loading" style="width: 100%">
        <el-table-column label="ID" prop="id" width="80" />
        <el-table-column label="Title" min-width="260">
          <template slot-scope="{ row }">
            <span>{{ title(row) }}</span>
            <el-tag v-if="isDuplicateAlias(row)"
                    type="warning"
                    size="mini"
                    class="ml-8">duplicate slug</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="Slug" min-width="200">
          <template slot-scope="{ row }">
            <code>{{ row.alias }}</code>
          </template>
        </el-table-column>
        <el-table-column label="Languages" min-width="180">
          <template slot-scope="{ row }">
            <el-tag v-for="lang in titledLanguages(row)"
                    :key="lang"
                    size="mini"
                    class="mr-4">{{ lang.toUpperCase() }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="Status" width="110">
          <template slot-scope="{ row }">
            <el-tag :type="row.is_hide ? 'danger' : 'success'" size="mini">
              {{ row.is_hide ? 'Hidden' : 'Visible' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="Updated" width="160">
          <template slot-scope="{ row }">
            {{ updatedAt(row) }}
          </template>
        </el-table-column>
        <el-table-column fixed="right" width="80">
          <template slot-scope="{ row }">
            <el-button size="mini" @click="editPage(row)">Edit</el-button>
          </template>
        </el-table-column>
      </el-table>
    </card>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';

export default {
  name: 'DocsList',
  data: () => ({
    loading: false,
    search: '',
    visibility: 'all',
  }),
  computed: {
    ...mapState('docs', ['docs']),

    /**
     * The catalogue comes from the legacy table, which has no unique constraint on the slug —
     * unlike the store the pages are saved into. A page sharing a slug cannot be saved without
     * overwriting the other one, so it is flagged rather than left to fail on save.
     */
    duplicateAliases() {
      const seen = {};
      this.docs.forEach((doc) => {
        seen[doc.alias] = (seen[doc.alias] || 0) + 1;
      });

      return Object.keys(seen).filter((alias) => seen[alias] > 1);
    },
    filteredDocs() {
      const needle = this.search.trim().toLowerCase();

      return this.docs.filter((doc) => {
        if (this.visibility === 'visible' && doc.is_hide) {
          return false;
        }
        if (this.visibility === 'hidden' && !doc.is_hide) {
          return false;
        }
        if (!needle) {
          return true;
        }

        const haystack = [String(doc.id), doc.alias || '']
          .concat(Object.values(doc.title || {}))
          .join(' ')
          .toLowerCase();

        return haystack.includes(needle);
      });
    },
  },
  created() {
    this.load();
  },
  methods: {
    ...mapActions('docs', ['getDocs']),

    async load() {
      this.loading = true;
      try {
        await this.getDocs();
      } catch (error) {
        this.$notify({ type: 'warning', title: 'Could not load static pages' });
      } finally {
        this.loading = false;
      }
    },

    /**
     * Falls through the languages an editor is most likely to read: the legacy catalogue always
     * has Russian, English only for about half of the pages.
     */
    title(doc) {
      const titles = doc.title || {};

      return titles.en || titles.ru || titles.ua || doc.alias || `#${doc.id}`;
    },
    titledLanguages(doc) {
      const titles = doc.title || {};

      return Object.keys(titles).filter((lang) => Boolean(titles[lang]));
    },
    isDuplicateAlias(doc) {
      return this.duplicateAliases.includes(doc.alias);
    },
    updatedAt(doc) {
      if (!doc.updated_at) {
        return '—';
      }

      return new Date(doc.updated_at).toLocaleDateString();
    },
    editPage(doc) {
      this.$router.push({ name: 'StaticPage', params: { id: doc.id } });
    },
    createPage() {
      this.$router.push({ name: 'StaticPage', params: { id: 'new' } });
    },
  },
};
</script>

<style scoped>
.ml-8 {
  margin-left: 8px;
}

.mr-4 {
  margin-right: 4px;
}
</style>
